<script lang="ts">
import type { Rating } from '$types/card';

interface Props {
  onRate: (r: Rating) => void;
  disabled?: boolean;
  showShortcuts?: boolean;
  /** Echte nächste-Fälligkeit-Labels je Rating (fsrs.previewIntervals → stats.dueHint). */
  hints?: Partial<Record<Rating, string>>;
}

const { onRate, disabled = false, showShortcuts = true, hints = {} }: Props = $props();

const RATINGS: { r: Rating; label: string; cls: string; key: string }[] = [
  { r: 1, label: 'again', cls: 'r-again', key: '1' },
  { r: 2, label: 'hard', cls: 'r-hard', key: '2' },
  { r: 3, label: 'good', cls: 'r-good', key: '3' },
  { r: 4, label: 'easy', cls: 'r-easy', key: '4' },
];
</script>

<div class="rating-bar" role="group" aria-label="FSRS-Bewertung">
  {#each RATINGS as ri}
    <button
      type="button"
      class={'rate ' + ri.cls}
      {disabled}
      aria-label={`${ri.label}${hints[ri.r] ? ` — nächste Wiederholung ${hints[ri.r]}` : ''}`}
      onclick={() => onRate(ri.r)}>
      <span class="rate-num">{ri.r}</span>
      <span class="rate-label">{ri.label}</span>
      <span class="rate-hint">{hints[ri.r] ?? ''}</span>
      {#if showShortcuts}<span class="rate-kbd">{ri.key}</span>{/if}
    </button>
  {/each}
</div>

<style>
  .rating-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    margin-top: 14px;
  }
  .rate {
    border: 2px solid;
    background: transparent;
    padding: 12px 6px 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-height: 64px;
    font-family: var(--font-mono);
    position: relative;
    transition: transform 80ms ease;
  }
  .rate:not(:disabled):hover { transform: translateY(-2px); }
  .rate:not(:disabled):active { transform: translateY(0); }
  .rate:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    background: var(--paper-2);
  }
  .rate-num { font-size: 18px; font-weight: 800; font-variant-numeric: tabular-nums; line-height: 1; }
  .rate-label { font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .rate-hint { font-size: 9px; color: var(--muted); letter-spacing: 0.04em; }
  .rate-kbd {
    position: absolute;
    top: 4px; right: 4px;
    font-size: 9px;
    opacity: 0.5;
    border: 1px solid currentColor;
    padding: 1px 4px;
    border-radius: 2px;
  }
  .r-again { color: var(--r-again); border-color: var(--r-again); }
  .r-hard  { color: var(--r-hard);  border-color: var(--r-hard); }
  .r-good  { color: var(--r-good);  border-color: var(--r-good); }
  .r-easy  { color: var(--r-easy);  border-color: var(--r-easy); }
  .rate:not(:disabled).r-again:hover { background: var(--r-again); color: #fff; }
  .rate:not(:disabled).r-hard:hover  { background: var(--r-hard);  color: #fff; }
  .rate:not(:disabled).r-good:hover  { background: var(--r-good);  color: #fff; }
  .rate:not(:disabled).r-easy:hover  { background: var(--r-easy);  color: #fff; }

  @media (max-width: 480px) {
    .rate-hint { display: none; }
    .rate { min-height: 56px; padding: 8px 4px; }
  }
</style>
